import { useState } from "react";
import { AI_DRAFT_LABEL } from "@nob/schemas";
import { ADJUST_COPY } from "../doorman/doorman-area";
import { Button, LinkButton, QuietButton, SecondaryButton } from "../ui/button";
import { AdvisoryNote, DiffRow } from "./diff-view";
import { groupDiff, sectionsTouched } from "../../lib/rules";
import type { DiffLine, SurfacedProposal } from "../../lib/api";

/**
 * House Rules proposals (M15, PRD FR-8.3). The feedback loop notices a pattern,
 * batches the signals, and surfaces one dry suggestion above the document —
 * never a silent write. The recipient stays in control of their own door: Add,
 * Adjust, or Dismiss. Where a proposal may pull against an existing line the
 * pair is shown as a note (ruling R-0905-6) and Add stays a plain Add.
 *
 * THE MIDDLE VERB IS ADJUST AND IT LEAVES THIS SCREEN (ruling R-0915-1 (4)).
 * It read "Edit" and opened the markdown editor on the proposed document. The
 * product's claim is that the owner never has to write `nob.md` by hand, so it
 * now opens the conversation with this card named and the composer waiting;
 * Nob answers with a revised card. The editor is still reachable from
 * Settings, for the owner who wants it, and nowhere else.
 *
 * AND THE CARD NAMES ITS SECTIONS, the same chips the conversation's batch card
 * wears, so the owner reads the same change the same way in both places.
 *
 * Every card here is a pure render of what it is handed; the network state
 * lives up in `RulesArea`.
 */

/** The added line a proposal is really about, without its bullet. */
export function firstAddedLine(lines: DiffLine[]): string {
  const add = lines.find((l) => l.type === "add");
  return add ? add.line.replace(/^\s*[-*+]\s+/, "").trim() : "";
}

/** The section the addition lands in — the nearest heading above it, or the default door. */
function sectionOf(lines: DiffLine[]): string {
  const addIndex = lines.findIndex((l) => l.type === "add");
  for (let i = addIndex; i >= 0; i -= 1) {
    const heading = /^#{1,6}\s+(.*)$/.exec((lines[i]?.line ?? "").trim());
    if (heading?.[1]?.trim()) return heading[1].trim();
  }
  return "What doesn't";
}

/**
 * The ask, phrased dryly from what triggered it (FR-8.3). An observed pattern or
 * an explicit instruction asks to add the line; a consolidation asks to merge;
 * a contradiction says nothing here, because the advisory note carries it.
 */
export function askFor(proposal: SurfacedProposal): string {
  switch (proposal.trigger_kind) {
    case "consolidation":
      return "Merge them into one rule?";
    case "contradiction":
      return "";
    case "action_pattern":
    case "explicit": {
      const line = firstAddedLine(proposal.diff_lines);
      if (!line) return "Add this to your rules?";
      return `Add to ${sectionOf(proposal.diff_lines)}: “${line}”?`;
    }
  }
}

/** The diff, collapsed to what changed — the exact rows the M5 review screen draws. */
function CardDiff({ lines }: { lines: DiffLine[] }) {
  const segments = groupDiff(lines);
  return (
    <div className="mt-3 overflow-hidden rounded-lg border border-border bg-surface/40">
      <div className="max-h-[40vh] overflow-y-auto py-3">
        {segments.map((segment, i) =>
          segment.kind === "gap" ? (
            <p
              key={`gap-${i}`}
              className="px-5 py-1 font-mono text-[11px] uppercase tracking-[0.14em] text-fainter"
            >
              {segment.count} unchanged {segment.count === 1 ? "line" : "lines"}
            </p>
          ) : (
            <DiffRow key={`line-${segment.index}`} line={segment.line} />
          ),
        )}
      </div>
    </div>
  );
}

type CardProps = {
  proposal: SurfacedProposal;
  /**
   * The document as it stands, which is the other half of "which sections does
   * this touch": a proposal holds the whole RESULTING document, so the answer
   * is the two of them compared — never the diff, whose context window very
   * often does not reach the heading a changed line sits under.
   */
  current: string;
  busy: boolean;
  /** Set the moment Dismiss lands: the card says so and fades on its way out. */
  dismissed: boolean;
  onAdd: () => void;
  /**
   * Adjust (ruling R-0915-1 (4)): the panel closes and the owner says what to
   * change in the conversation, with this card named. It opened the markdown
   * editor on the proposed document until the ruling reversed R-0914-2 (3).
   */
  onAdjust: () => void;
  onDismiss: () => void;
};

export function ProposalCard({
  proposal,
  current,
  busy,
  dismissed,
  onAdd,
  onAdjust,
  onDismiss,
}: CardProps) {
  const [open, setOpen] = useState(false);
  const ask = askFor(proposal);
  const sections = sectionsTouched(current, proposal.proposed_markdown);

  return (
    <article
      className={`rounded-xl border border-border bg-surface/30 p-5 transition-opacity duration-500 ${
        dismissed ? "opacity-40" : "opacity-100"
      }`}
    >
      {sections.length > 0 && (
        <div data-testid="proposal-sections" className="mb-2 flex flex-wrap items-center gap-1.5">
          {sections.map((name) => (
            <span
              key={name}
              className="inline-flex h-5 items-center rounded-full border border-border px-2
                         font-mono text-[10px] uppercase tracking-[0.12em] text-faint"
            >
              {name}
            </span>
          ))}
        </div>
      )}
      {proposal.evidence_summary && (
        <p className="text-[15px] leading-relaxed text-text-soft">{proposal.evidence_summary}</p>
      )}
      {ask && <p className="mt-2 text-[15px] leading-relaxed text-muted">{ask}</p>}
      {/* AI Act Art. 50(2), ruling R-0914-1 (11). Edit drops this proposal into
          the rules editor, where it stops looking like a suggestion and starts
          looking like the owner's own document — which is exactly the moment
          the label has to already have been read. */}
      <p className="mt-2 text-[11px] text-fainter">{AI_DRAFT_LABEL}</p>

      {proposal.diff_lines.length > 0 && (
        <div className="mt-3">
          <LinkButton type="button" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide the change" : "See the change"}
          </LinkButton>
          {open && <CardDiff lines={proposal.diff_lines} />}
        </div>
      )}

      {proposal.conflicts.length > 0 && (
        <div className="mt-4">
          <AdvisoryNote conflicts={proposal.conflicts} />
        </div>
      )}

      {dismissed ? (
        <p className="nob-ack mt-4 font-mono text-[11px] text-faint">Dismissed.</p>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button type="button" onClick={onAdd} disabled={busy}>
            Add
          </Button>
          <SecondaryButton type="button" onClick={onAdjust} disabled={busy}>
            {ADJUST_COPY}
          </SecondaryButton>
          <QuietButton type="button" onClick={onDismiss} disabled={busy}>
            Dismiss
          </QuietButton>
        </div>
      )}
    </article>
  );
}

type SectionProps = {
  proposals: SurfacedProposal[];
  /** The document the proposals below are changes TO. See `CardProps.current`. */
  current: string;
  busy: Record<string, boolean>;
  dismissed: Record<string, boolean>;
  onAdd: (proposal: SurfacedProposal) => void;
  onAdjust: (proposal: SurfacedProposal) => void;
  onDismiss: (proposal: SurfacedProposal) => void;
};

/**
 * The quiet section above the document (FR-8.3). Absent entirely when nothing is
 * waiting — a door with nothing to tune says nothing. No red dots, no nagging:
 * a heading, a count, and the cards.
 */
export function ProposalsSection({
  proposals,
  current,
  busy,
  dismissed,
  onAdd,
  onAdjust,
  onDismiss,
}: SectionProps) {
  if (proposals.length === 0) return null;
  const waiting = proposals.filter((p) => !dismissed[p.id]).length;

  return (
    <section className="mt-6">
      <p className="text-[0.8rem] font-medium uppercase tracking-[0.18em] text-faint">
        Nob has noticed
        {waiting > 0 && <span className="ml-2 font-mono text-fainter">&middot;{waiting}</span>}
      </p>

      <div className="mt-4 space-y-3">
        {proposals.map((proposal) => (
          <ProposalCard
            key={proposal.id}
            proposal={proposal}
            current={current}
            busy={busy[proposal.id] ?? false}
            dismissed={dismissed[proposal.id] ?? false}
            onAdd={() => onAdd(proposal)}
            onAdjust={() => onAdjust(proposal)}
            onDismiss={() => onDismiss(proposal)}
          />
        ))}
      </div>
    </section>
  );
}
